import CreativeEngine from "@cesdk/engine";
import {useEffect, useMemo, useRef, type Ref, type RefObject, useState, useCallback} from "react";
import {
    PRESENTATION_HEIGHT,
    PRESENTATION_WIDTH
} from "../constants.ts";
import CreativeEditorSDK from '@cesdk/cesdk-js';
import type {DesignBlockId, ReplaceAssetLibraryEntriesContext} from "@cesdk/cesdk-js";
export {findOwningPage} from "./slideUtilities.ts";


declare global {
    interface Window {
        createjs?: any;
        AdobeAn?: any;
    }
}


export async function injectStyles() {
    const rootEl = document.getElementById('root-shadow');
    const shadowRoot: ShadowRoot | null | undefined = rootEl?.shadowRoot;
    if (!shadowRoot) return;

    const css = await getCssTextFromUrl("/css/cesdk-custom.css");

    let styleEl = shadowRoot.querySelector<HTMLStyleElement>("#custom-styles");
    if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = "custom-styles";
        shadowRoot.appendChild(styleEl);
    }
    styleEl.textContent = css;

}

async function getCssTextFromUrl(url: string): Promise<string> {
    const res = await fetch(url, { cache: "force-cache" });
    if (!res.ok) return "";
    return await res.text();
}

/**
 * Set the styles for overlay plugins
 */
export const setOverlayPluginStyles = () => {

    let styleSheet = document.body.querySelector('#custom-overlay-styles');
    if (!styleSheet) {
        styleSheet = document.createElement('style');
        styleSheet.id = 'custom-overlay-styles';
        styleSheet.textContent = `
        /*Custom HTML overlay Slider*/
        .idecide-range{ -webkit-appearance:none; appearance:none; background:transparent; }
        .idecide-range:focus{ outline:none; }
        .idecide-range::-webkit-slider-runnable-track{ background:transparent; height:var(--track-h, 36px); }
        .idecide-range::-webkit-slider-thumb{
            -webkit-appearance:none; appearance:none; background:var(--thumb-color, #FFFFFF);
            border:var(--thumb-ring, 2px) solid #FFFFFF;
            width:var(--thumb-size, 32px); height:var(--thumb-size, 32px); border-radius:50%;
            box-shadow: 0 2px 8px rgba(0,0,0,0.35), 0 0 0 2px rgba(255,255,255,0.55) inset;
            margin-top: calc((var(--track-h, 36px) - var(--thumb-size, 32px)) / 2 + var(--thumb-nudge, 0px));
        }
        .idecide-range::-moz-range-track{ background:transparent; height:var(--track-h, 36px); }
        .idecide-range::-moz-range-thumb{
            background:var(--thumb-color, #FFFFFF);
            border:var(--thumb-ring, 2px) solid #FFFFFF;
            width:var(--thumb-size, 32px); height:var(--thumb-size, 32px); border-radius:50%;
            box-shadow: 0 2px 8px rgba(0,0,0,0.35), 0 0 0 2px rgba(255,255,255,0.55) inset;
        }
            `;

        document.body.appendChild(styleSheet);
    }
}

//video timeline adjustments
const setVideoTimelineStyles = () =>  {
    const shadowHostSelector = "#root-shadow";
    const timelineSelector = 'div[role="region"][aria-label="Video Timeline"]';

    function getShadowRoot() {
        const host = document.querySelector(shadowHostSelector);
        return host && host.shadowRoot ? host.shadowRoot : null;
    }

    function getTimelineEl() {
        const sr = getShadowRoot();
        return sr ? sr.querySelector(timelineSelector) : null;
    }

    // Set CSS variable on :root
    function updateTimelineVar() {
        const tl = getTimelineEl();
        if (!tl) return;
        const h = tl.getBoundingClientRect().height;
        document.documentElement.style.setProperty("--timeline-height", `${h}px`);
    }

    function observeTimeline(tl:any) {
        if (tl.__bound) return;
        tl.__bound = true;

        if (window.ResizeObserver) {
            const ro = new ResizeObserver(updateTimelineVar);
            ro.observe(tl);
        }
        window.addEventListener("resize", updateTimelineVar);
    }

    function waitForTimeline() {
        const tryInit = () => {
            const tl = getTimelineEl();
            if (tl) {
                updateTimelineVar();
                observeTimeline(tl);
                return true;
            }
            return false;
        };

        if (tryInit()) return;

        const obs = new MutationObserver(() => {
            if (tryInit()) obs.disconnect();
        });
        obs.observe(document.documentElement, { childList: true, subtree: true });
    }

    waitForTimeline();
};

// wait for the cesdk element to actually be in the DOM
async function waitForCanvas(timeoutMs = 5000): Promise<HTMLElement | null> {
    const el = document.querySelector('cesdk-canvas') as HTMLElement | null;
    if (el) return el;

    return new Promise((resolve) => {
        const deadline = performance.now() + timeoutMs;
        const mo = new MutationObserver(() => {
            const found = document.querySelector('cesdk-canvas') as HTMLElement | null;
            if (found) {
                mo.disconnect();
                resolve(found);
            } else if (performance.now() > deadline) {
                mo.disconnect();
                resolve(null);
            }
        });
        mo.observe(document.documentElement, { childList: true, subtree: true });
    });
}

/**
 * Override Img.ly styles for Review screen
 */
export const applyPlayerStyleOverrides = async () => {

    await injectStyles();
    setOverlayPluginStyles();
}

/**
 * Generate filename with current date and time for the given extension
 */
export const getDateTimeFileName = (extension: string) => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);

    // Format time as 12-hour with am/pm
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12;

    const filename = `${day}-${month}-${year}, ${hours}:${minutes} ${ampm}.${extension}`;

    return filename;
}

/**
 * Utility function that returns if the Imgly engine is ready to be interacted with.
 * @param engine
 */
export function isEngineReady(engine: any): boolean {
    return !!engine
        && !!engine.block && !!engine.scene && !!engine.event && !!engine.editor
        && typeof engine.block.findAllSelected === 'function'
        && typeof engine.scene.getCurrentPage === 'function';
}

/**
 * Load a JS script once; reuse if it’s already on the page
 */
export function loadScriptOnce(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        // Already present?
        if ([...document.scripts].some(s => s.src === src)) return resolve();

        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

export const getCJS = () => window.createjs;
export const getAN  = () => window.AdobeAn;

/**
 * Wait until AdobeAn has a composition by id (slide.js registers it)
 */
export function waitForComposition(id: string, timeoutMs = 10000): Promise<any> {
    return new Promise((resolve, reject) => {
        const start = performance.now();
        (function poll() {
            const AdobeAn = getAN();
            if (AdobeAn?.getComposition) {
                const comp = AdobeAn.getComposition(id);
                if (comp) return resolve(comp);
            }
            if (performance.now() - start > timeoutMs) {
                return reject(new Error(`AdobeAn composition "${id}" not found`));
            }
            requestAnimationFrame(poll);
        })();
    });
}

/**
 * Function to make the Adobe animate animation responsive to the given host element.
 * @param stage
 * @param design
 * @param hostEl
 * @param containers
 * @param mode
 */
export function makeResponsiveToHostUsingDesign(
    stage: any,
    design: { designW: number; designH: number },
    hostEl: HTMLElement,
    containers: (HTMLCanvasElement | HTMLElement | null)[],
    mode: 'contain' | 'cover' | 'stretch' = 'stretch'
): () => void {
    const baseW = Number(design.designW);
    const baseH = Number(design.designH);
    const canvas = containers[0] as HTMLCanvasElement;

    const resize = () => {
        const rect = hostEl.getBoundingClientRect();
        const iw = Math.max(1, rect.width);
        const ih = Math.max(1, rect.height);
        const p  = window.devicePixelRatio || 1;

        const rx = iw / baseW;
        const ry = ih / baseH;
        const s =
            mode === 'cover'   ? Math.max(rx, ry) :
                mode === 'contain' ? Math.min(rx, ry) :
                    undefined; // 'stretch' → non-uniform

        const cssW = mode === 'stretch' ? iw : baseW * (s as number);
        const cssH = mode === 'stretch' ? ih : baseH * (s as number);

        containers.forEach((c) => {
            const el = c as HTMLElement;
            el.style.width  = `${cssW}px`;
            el.style.height = `${cssH}px`;
        });

        if (mode === 'stretch') {
            canvas.width  = Math.max(1, Math.round(baseW * p * rx));
            canvas.height = Math.max(1, Math.round(baseH * p * ry));
            stage.scaleX  = p * rx;
            stage.scaleY  = p * ry;
        } else {
            const ds = p * (s as number);
            canvas.width  = Math.max(1, Math.round(baseW * ds));
            canvas.height = Math.max(1, Math.round(baseH * ds));
            stage.scaleX  = ds;
            stage.scaleY  = ds;
        }

        if (typeof stage.updateViewport === 'function') {
            stage.updateViewport(canvas.width, canvas.height); // StageGL safety
        }

        stage.tickOnUpdate = false;
        stage.update();
        stage.tickOnUpdate = true;

    };

    const ro = new ResizeObserver(resize);
    ro.observe(hostEl);
    resize();
    return () => ro.disconnect();
}

let silenceLoopAudio: HTMLAudioElement | null = null;

/**
 * Start or resume the iOS silence track that allows sound output with ringer switch off.
 * Safe to call multiple times.
 * Resolves when play() resolves (or immediately if already playing).
 */
export async function startBackgroundSilence(): Promise<void> {
    console.log('startBackgroundSilence');
    if (!silenceLoopAudio) {
        const silenceDataURL =
            "data:audio/mp3;base64,//MkxAAHiAICWABElBeKPL/RANb2w+yiT1g/gTok//lP/W/l3h8QO/OCdCqCW2Cw//MkxAQHkAIWUAhEmAQXWUOFW2dxPu//9mr60ElY5sseQ+xxesmHKtZr7bsqqX2L//MkxAgFwAYiQAhEAC2hq22d3///9FTV6tA36JdgBJoOGgc+7qvqej5Zu7/7uI9l//MkxBQHAAYi8AhEAO193vt9KGOq+6qcT7hhfN5FTInmwk8RkqKImTM55pRQHQSq//MkxBsGkgoIAABHhTACIJLf99nVI///yuW1uBqWfEu7CgNPWGpUadBmZ////4sL//MkxCMHMAH9iABEmAsKioqKigsLCwtVTEFNRTMuOTkuNVVVVVVVVVVVVVVVVVVV//MkxCkECAUYCAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

        silenceLoopAudio = document.createElement("audio");
        silenceLoopAudio.controls = false;
        silenceLoopAudio.preload = "auto";
        silenceLoopAudio.loop = true;
        silenceLoopAudio.src = silenceDataURL;
        // keep audio element off-screen / not visible — don't append to DOM unless necessary
    }

    // If already playing, resolve immediately
    try {
        const p = silenceLoopAudio.play();
        if (p instanceof Promise) {
            try {
                await p;
                console.log('silence played success');
            } catch (err) {
                console.warn("silence play failed", err);
                return await Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    } catch (err) {
        console.warn("silence play threw", err);
        return Promise.resolve();
    }
}

export function splitName(fullName: string): [string, string] {
    const parts = fullName
        .split(' ')
        .map(p => p.trim())
        .filter(p => p.length > 0);

    if (parts.length === 0) {
        return ["", ""];
    }

    if (parts.length === 1) {
        return [parts[0], ""];
    }

    return [parts[0], parts.slice(1).join(' ')];
}